import type { FC } from 'react';
import c from 'classnames';
import { PapiSellerDiscount } from '@wix/da-papi-types';
import DiscountCategoryLabels from '../../DiscountCategoryLabels';
import {
  DATE_FORMATS,
  formatDate,
  formatTime,
  TIME_FORMATS,
} from '@wix/da-i18next/pkg/time/functions';
import { useTranslation } from 'react-i18next';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiData,
  DuplicateDiscountClickBiEvent,
  EndDiscountClickBiEvent,
} from '@wix/da-bi/pkg/events';
import MoreButton from '../../DiscountRow/MoreButton';
import {
  useDeleteDiscount,
  useDiscountRowActions,
  useEndDiscount,
} from '../../SellerDiscountModal/hooks';
import SellerDiscountModal from '../../SellerDiscountModal';
import { useSelector } from 'react-redux';
import { getActiveSection } from '../../../../../selectors/studio';
import { isDiscountActive } from '../../helpers';

import s from './DiscountRowMobile.scss';

interface Props {
  discount: PapiSellerDiscount;
  className?: string;
}

const DiscountRowMobile: FC<Props> = ({ discount, className }) => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const activeSection = useSelector(getActiveSection);
  const isActive = isDiscountActive(discount);
  const {
    isModalOpen,
    modalMode,
    openEditModal,
    openDuplicateModal,
    closeModal,
  } = useDiscountRowActions(discount.discountId);
  const { deleteDiscount } = useDeleteDiscount();
  const handleDelete = () => {
    deleteDiscount(discount.discountId);
  };
  const { endDiscount } = useEndDiscount();
  const handleEndDiscount = () => {
    logBiEvent(
      BiData<EndDiscountClickBiEvent>({
        evid: 453,
        campaignid: discount.discountId,
      })
    );
    endDiscount(discount.discountId);
  };
  const handleDuplicateDiscount = () => {
    logBiEvent(
      BiData<DuplicateDiscountClickBiEvent>({
        evid: 456,
        campaignid: discount.discountId,
      })
    );
    openDuplicateModal();
  };

  const prettyStartDate = formatDate(
    discount.startDate,
    DATE_FORMATS.Date.Short
  );
  const prettyStartTime = formatTime(
    discount.startDate,
    TIME_FORMATS.HourMinute
  )
    .replace(/:00/, '')
    .toLowerCase();

  const prettyEndDate = formatDate(discount.endDate, DATE_FORMATS.Date.Short);
  const prettyEndTime = formatTime(discount.endDate, TIME_FORMATS.HourMinute)
    .replace(/:00/, '')
    .toLowerCase();

  const isFirstMonthOnly = discount.appliesto === 'any_tier';

  return (
    <div className={c(s['root'], className)}>
      <div className={s['left-side']}>
        <div className={s['name']}>{discount.name}</div>
        <DiscountCategoryLabels
          className={s['discount-labels']}
          discount={discount}
        />
        <div className={s['dates']}>
          <div className={s['start-date']}>{prettyStartDate}</div>
          <div className={s['start-time']}>{prettyStartTime}</div>
          <div className={s['end-date']}>{prettyEndDate}</div>
          <div className={s['end-time']}>{prettyEndTime}</div>
        </div>
        <div className={s['discount']}>
          <div className={s['discount-percent']}>
            {discount.amount?.percent}%
          </div>
          <div className={s['discount-off-label']}>
            {t(
              `studio.discountRow.offLabel${isFirstMonthOnly ? '.firstMonthOnly' : ''}`
            )}
          </div>
        </div>
      </div>
      <MoreButton
        discount={discount}
        isActive={isActive}
        activeSection={activeSection}
        onEditClick={openEditModal}
        onDuplicateClick={handleDuplicateDiscount}
        onDeleteClick={handleDelete}
        onEndDiscountClick={handleEndDiscount}
      />
      {isModalOpen && (
        <SellerDiscountModal
          discount={discount}
          mode={modalMode}
          onRequestClose={closeModal}
        />
      )}
    </div>
  );
};
DiscountRowMobile.displayName = 'DiscountRowMobile';

export default DiscountRowMobile;
